import React from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { ListItem, Box, ListItemText, Typography, Button } from "@mui/material";
import PropTypes from "prop-types";
import CourseActivityIcon from "../Icons/CourseActivityIcon";
import * as constants from "../../constants";
import HighlightedTitle from "./HighlightedTitle";
import { useTranslation } from "react-i18next";
import { activityTypes } from "../../constants/index";

const useStyles = makeStyles(theme => ({
  icon: {
    display: "flex",
    alignSelf: "flex-start",
    paddingRight: theme.spacing(4.5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 0.5, 0, 0),
      minWidth: theme.spacing(5)
    }
  },
  activityButton: {
    minWidth: 88,
    [theme.breakpoints.down("sm")]: {
      maxWidth: 88
    },
    "& > button": {
      width: "100%"
    }
  },
  progressText: {
    textAlign: "center",
    flex: "1",
    marginRight: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      textAlign: "left"
    }
  },
  listItem: {
    padding: theme.spacing(1.25, 0),
    borderTopWidth: "1px",
    borderTopColor: theme.palette.secondary[300],
    borderTopStyle: "solid",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      paddingTop: theme.spacing(2)
    }
  }
}));

function CourseActivity({
  onGoToActivityClick,
  title,
  contentableType,
  searchInput,
  status,
  submittedQuiz,
  description,
  showActivityButton,
  activityStatusText,
  isSelfAssignedPolicy
}) {
  const { t } = useTranslation();

  const playButton = (
    <Button variant="contained" color="primary" onClick={onGoToActivityClick}>
      {t("coursePlayer.couseActivity.playBtn")}
    </Button>
  );

  const resumeButton = (
    <Button variant="outlined" color="primary" onClick={onGoToActivityClick}>
      {t("coursePlayer.couseActivity.resumeBtn")}
    </Button>
  );

  const revisitButton = (
    <Button color="primary" onClick={onGoToActivityClick}>
      {t("coursePlayer.couseActivity.revisitBtn")}
    </Button>
  );

  const startButton = (
    <Button variant="contained" color="primary" onClick={onGoToActivityClick}>
      {t("coursePlayer.couseActivity.startBtn")}
    </Button>
  );

  function createActivityButton(activityStatus, submitted) {
    if (isSelfAssignedPolicy) {
      return playButton;
    } else if (
      activityStatus &&
      activityStatus.toLowerCase() === constants.statusInProgress.toLowerCase()
    ) {
      return resumeButton;
    } else if (
      (activityStatus &&
        activityStatus.toLowerCase() ===
          constants.statusCompleted.toLowerCase()) ||
      submitted
    ) {
      return revisitButton;
    } else if (
      activityStatus &&
      activityStatus.toLowerCase() === constants.statusNotStarted.toLowerCase()
    ) {
      return startButton;
    }
  }

  function getActivityDescription() {
    if (contentableType === activityTypes.homework) {
      return "";
    }

    return description;
  }

  const theme = useTheme();
  const classes = useStyles();

  return (
    <ListItem classes={{ root: classes.listItem }}>
      <Box
        display="flex"
        flex={1}
        flexDirection={{ xs: "column", sm: "row" }}
        width="100%"
      >
        <Box
          display="flex"
          flex="3"
          alignItems="center"
          color={theme.palette.text.secondary}
          ml={{ xs: 0, sm: 2 }}
          py={{ xs: 0.5, sm: 1.5 }}
        >
          {contentableType !== activityTypes.json && (
            <div className={classes.icon}>
              <CourseActivityIcon type={contentableType} />
            </div>
          )}
          <ListItemText
            disableTypography
            style={{ margin: 0 }}
            primary={
              <HighlightedTitle
                title={title}
                color={theme.palette.text.primary}
                searchInput={searchInput}
              />
            }
            secondary={
              <Box color={theme.palette.text.secondary}>
                <Typography
                  variant="body2"
                  dangerouslySetInnerHTML={{ __html: getActivityDescription() }}
                />
              </Box>
            }
          />
        </Box>
        <Box
          display="flex"
          flex={2}
          justifyContent="flex-end"
          pt={{ xs: 3, sm: 0 }}
          pb={{ xs: 1.5, sm: 0 }}
          mr={{ xs: 1.5, sm: 0 }}
          alignItems="center"
          ml={4}
          alignSelf="stretch"
        >
          {!!activityStatusText && (
            <Typography
              classes={{ root: classes.progressText }}
              noWrap
              color="textSecondary"
            >
              {activityStatusText}
            </Typography>
          )}
          {showActivityButton && (
            <div className={classes.activityButton}>
              {createActivityButton(status, submittedQuiz)}
            </div>
          )}
        </Box>
      </Box>
    </ListItem>
  );
}

export default CourseActivity;

CourseActivity.propTypes = {
  title: PropTypes.string.isRequired,
  contentableType: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  searchInput: PropTypes.string,
  onGoToActivity: PropTypes.func,
  showActivityButton: PropTypes.bool
};

CourseActivity.defaultProps = {
  showActivityButton: true
};
